import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Backdrop,
  Tooltip,
  Chip,
  TextField,
} from "@mui/material";
import {
  GroupAdd,
  DeleteOutline,
  Visibility as VisibilityIcon,
  ExpandMore,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Chat as ChatIcon,
  PersonAdd as PersonAddIcon,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { blue } from "@mui/material/colors";
import axiosInstance from "../../../api/axiosInstance";
import base_url from "../../../constants";
import useNotification from "../../common/Notification";
import WhatsappBusinessGroupCreate from "./WhatsappBusinessGroupCreate";
import WhatsappBusinessGroupEdit from "./WhatsappBusinessGroupEdit";
import WhatsappBusinessGroupDeleteContactsModal from "./WhatsappBusinessGroupDeleteContactsModal";
import WhatsappBusinessGroupDeleteGroupModal from "./WhatsappBusinessGroupDeleteGroupModal";
import WhatsappBusinessGroupDeleteContactModal from "./WhatsappBusinessGroupDeleteContactModal";
import { WB_SX, WB_UI } from "../../../ui/ux/uiTokens";

const ContactDetailsSearch = () => {
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [deleteContactModalOpen, setDeleteContactModalOpen] = useState(false);
  const [deleteContactGroupName, setDeleteContactGroupName] = useState("");
  const notify = useNotification();

  const fetchContactDetails = async (phoneValue) => {
    const value = phoneValue.trim();
    if (!value) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await axiosInstance.get(
        `${base_url}/whatsapp-business/groups/contact-details`,
        { params: { phone_number: value } }
      );
      if (res.data?.success) {
        setResult(res.data.data || null);
      } else {
        notify(res.data?.message || "No details found for this contact", "info");
        setResult(null);
      }
    } catch (e) {
      notify(
        e.response?.data?.message || "Failed to fetch contact details",
        "error"
      );
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!phone.trim()) {
      notify("Please enter a phone number", "warning");
      return;
    }
    await fetchContactDetails(phone);
  };

  const handleDeleteFromGroup = (groupName) => {
    if (!phone.trim()) {
      notify("Search a contact first before deleting from group.", "warning");
      return;
    }
    setDeleteContactGroupName(groupName);
    setDeleteContactModalOpen(true);
  };

  return (
    <>
    <Card
      elevation={0}
      sx={{
        mt: 1,
        mb: 2.5,
        borderRadius: 2,
        border: "1px solid rgba(0,0,0,0.08)",
        backgroundColor: "rgba(255,255,255,0.9)",
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 1.5,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Search Contact Across Groups
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Quickly find which groups a phone number belongs to.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
          <TextField
            size="small"
            label="Phone Number"
            placeholder="e.g. 919876543210"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
            }}
            sx={{ minWidth: { xs: "100%", sm: 260 } }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleSearch}
            disabled={loading}
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
          >
            {loading ? "Searching..." : "Search"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setPhone("");
              setResult(null);
              setDeleteContactModalOpen(false);
              setDeleteContactGroupName("");
            }}
            disabled={loading && !result}
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
          >
            Reset
          </Button>
        </Box>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        <AnimatePresence mode="wait">
          {!loading && result && (
            <motion.div
              key={result.phone_number || "result"}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.05 }}
              >
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                  Phone: {result.phone_number}
                </Typography>
              </motion.div>
              {Array.isArray(result.groups) && result.groups.length > 0 ? (() => {
                const groups = result.groups;
                const first = groups[0] || {};
                const latestUpdatedTs = groups.reduce((maxTs, g) => {
                  if (!g.updated_at) return maxTs;
                  const ts = new Date(g.updated_at).getTime();
                  return Number.isNaN(ts) ? maxTs : Math.max(maxTs, ts);
                }, 0);
                const latestUpdated = latestUpdatedTs
                  ? new Date(latestUpdatedTs).toLocaleString()
                  : "—";
                const lastSent =
                  result.last_message_sent_at
                    ? new Date(result.last_message_sent_at).toLocaleString()
                    : "—";
                const lastReceived =
                  result.last_message_received_at
                    ? new Date(result.last_message_received_at).toLocaleString()
                    : "—";
                const containerVariants = {
                  hidden: { opacity: 0 },
                  visible: {
                    opacity: 1,
                    transition: {
                      staggerChildren: 0.07,
                      delayChildren: 0.06,
                    },
                  },
                };
                const itemVariants = {
                  hidden: { opacity: 0, y: 8 },
                  visible: { opacity: 1, y: 0 },
                };
                return (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                  >
                    <Paper
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        p: 2,
                        mt: 1,
                        backgroundColor: "rgba(255,255,255,0.95)",
                      }}
                    >
                      <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        <motion.div variants={itemVariants}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            justifyContent: "space-between",
                            gap: 1.5,
                            mb: 1.5,
                          }}
                        >
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              Contact Name
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {first.contact_person_name || "—"}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              Last Message Sent At
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {lastSent}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              Last Message Received At
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {lastReceived}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              Last Updated
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {latestUpdated}
                            </Typography>
                          </Box>
                        </Box>
                        </motion.div>

                        <motion.div variants={itemVariants} style={{ marginBottom: 12 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                          Groups
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                          {groups.map((g, idx) => (
                            <motion.div
                              key={g.group_name + idx}
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{
                                delay: 0.12 + idx * 0.04,
                                duration: 0.25,
                              }}
                              style={{ display: "inline-flex", alignItems: "center" }}
                            >
                              <Box
                                sx={{ display: "flex", alignItems: "center", gap: 0.25 }}
                              >
                                <Chip
                                  label={g.group_name}
                                  size="small"
                                  sx={{
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    backgroundColor: idx % 3 === 0
                                      ? "rgba(25,118,210,0.08)"
                                      : idx % 3 === 1
                                      ? "rgba(46,125,50,0.08)"
                                      : "rgba(156,39,176,0.08)",
                                    color: idx % 3 === 0
                                      ? "primary.main"
                                      : idx % 3 === 1
                                      ? "success.main"
                                      : "secondary.main",
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteFromGroup(g.group_name)}
                                  disabled={loading || deleteContactModalOpen}
                                >
                                  <DeleteOutline fontSize="small" />
                                </IconButton>
                              </Box>
                            </motion.div>
                          ))}
                        </Box>
                        </motion.div>

                        <motion.div variants={itemVariants}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                            Summary
                          </Typography>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                            <Chip
                              size="small"
                              label={`Groups: ${groups.length}`}
                            />
                          </Box>
                        </motion.div>
                      </motion.div>
                    </Paper>
                  </motion.div>
                );
              })() : (
                <motion.div
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.08 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    This contact is not part of any groups.
                  </Typography>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
    <WhatsappBusinessGroupDeleteContactModal
      open={deleteContactModalOpen}
      onClose={() => {
        setDeleteContactModalOpen(false);
        setDeleteContactGroupName("");
      }}
      groupName={deleteContactGroupName}
      phoneNumbers={phone.trim() ? [phone.trim()] : []}
      onSuccess={() => fetchContactDetails(phone)}
    />
  </>
  );
};

const WhatsappBusinessGroupsManage = () => {
  const [groups, setGroups] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [backdropOpen, setBackdropOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteContactsOpen, setDeleteContactsOpen] = useState(false);
  const [deleteContactsGroup, setDeleteContactsGroup] = useState("");
  const [deleteGroupOpen, setDeleteGroupOpen] = useState(false);
  const [deleteGroupName, setDeleteGroupName] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editInitialGroup, setEditInitialGroup] = useState("");
  const notify = useNotification();

  const fetchGroups = async (page = 1) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `${base_url}/whatsapp-business/groups`,
        { params: { per_page: 20, page } }
      );
      if (res.data?.success) {
        setGroups(Array.isArray(res.data.data) ? res.data.data : []);
        setPagination(res.data.pagination || pagination);
      } else {
        setGroups([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        notify(res.data?.message || "Failed to load groups", "error");
      }
    } catch (e) {
      notify(
        e.response?.data?.message || "Failed to load groups",
        "error"
      );
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (_, value) => {
    fetchGroups(value);
  };

  const handleDeleteGroup = async (groupName) => {
    if (!window.confirm(`Delete group "${groupName}" and all its contacts?`)) {
      return;
    }
    setBackdropOpen(true);
    try {
      const res = await axiosInstance.delete(
        `${base_url}/whatsapp-business/groups/${encodeURIComponent(
          groupName
        )}`
      );
      if (res.data?.status === "success" || res.data?.success) {
        notify(res.data?.message || "Group deleted", "success");
        fetchGroups(pagination.current_page || 1);
      } else {
        notify(res.data?.message || "Failed to delete group", "error");
      }
    } catch (e) {
      notify(
        e.response?.data?.message || "Failed to delete group",
        "error"
      );
    } finally {
      setBackdropOpen(false);
    }
  };

  const handleViewContacts = (groupName) => {
    const url = `/whatsapp-business/groups/${encodeURIComponent(
      groupName
    )}/contacts`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleViewChats = () => {
    window.open("/whatsapp-business/chat-window", "_blank", "noopener,noreferrer");
  };

  return (
    <Box sx={WB_SX.page}>
      <Backdrop
        sx={(theme) => ({
          color: "#fff",
          zIndex: theme.zIndex.drawer + 1,
        })}
        open={backdropOpen}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={WB_SX.headerRow}>
        <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={WB_SX.pageTitle}
          >
            WhatsApp Business Groups
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your contact groups, view members, and keep things organised.
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="medium"
          startIcon={<GroupAdd />}
          component={motion.button}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setCreateOpen(true)}
          sx={{ flexShrink: 0, ...WB_SX.primaryButton }}
        >
          Create New Group
        </Button>
        <WhatsappBusinessGroupCreate
          opening={() => setCreateOpen(false)}
          closing={() => setCreateOpen(false)}
          open={createOpen}
          onSuccess={() => {
            setCreateOpen(false);
            fetchGroups(pagination.current_page || 1);
          }}
        />
      </Box>

      {/* Contact search just below header */}
      <ContactDetailsSearch />

      <Card
        elevation={0}
        sx={{
          mb: WB_UI.spacing.sectionGap,
          ...WB_SX.card,
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.5,
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Groups
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                size="small"
                label={`Total: ${pagination.total ?? groups.length ?? 0}`}
              />
              <Tooltip title="Refresh">
                <IconButton
                  size="small"
                  onClick={() => fetchGroups(pagination.current_page || 1)}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : groups.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 3, textAlign: "center" }}
            >
              No groups found.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={WB_SX.tableHeadCell}
                    >
                      Group Name
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={WB_SX.tableHeadCell}
                    >
                      Total Contacts
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={WB_SX.tableHeadCell}
                    >
                      Delete Contacts
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={WB_SX.tableHeadCell}
                    >
                      View Chats
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={WB_SX.tableHeadCell}
                    >
                      Expand Group
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={WB_SX.tableHeadCell}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups.map((g, idx) => (
                    <TableRow
                      key={g.group_name}
                      sx={{
                        ...WB_SX.tableRow,
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                          <Chip
                            label={g.group_name}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              letterSpacing: "0.04em",
                              borderRadius: 2,
                              px: 0.5,
                              backgroundColor: idx % 3 === 0
                                ? "rgba(25,118,210,0.08)"
                                : idx % 3 === 1
                                ? "rgba(46,125,50,0.08)"
                                : "rgba(156,39,176,0.08)",
                              color: idx % 3 === 0
                                ? "primary.main"
                                : idx % 3 === 1
                                ? "success.main"
                                : "secondary.main",
                            }}
                          />


                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            Last updated:{" "}
                            {g.updated_at
                              ? new Date(g.updated_at).toLocaleString()
                              : "—"}
                          </Typography>

                        </Box>


                      </TableCell>
                      <TableCell align="right">
                        {g.contact_count?.toLocaleString?.() ??
                          g.contact_count ??
                          0}
                      </TableCell>
                      {/* Last updated shown under group name instead of its own column */}
                      <TableCell align="center">
                        <Tooltip title="Delete contacts from group">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setDeleteContactsGroup(g.group_name);
                              setDeleteContactsOpen(true);
                            }}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View chats">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={handleViewChats}
                          >
                            <ChatIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Expand group (add contacts)">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setEditInitialGroup(g.group_name);
                              setEditOpen(true);
                            }}
                          >
                            <PersonAddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 0.5,
                            flexWrap: "wrap",
                          }}
                        >
                          <Tooltip title="View all contacts">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewContacts(g.group_name)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete entire group">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setDeleteGroupName(g.group_name);
                                setDeleteGroupOpen(true);
                              }}
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {pagination.last_page > 1 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 500 }}
              >
                Page {pagination.current_page} of {pagination.last_page}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
      <WhatsappBusinessGroupDeleteContactsModal
        open={deleteContactsOpen}
        onClose={() => setDeleteContactsOpen(false)}
        groupName={deleteContactsGroup}
        onSuccess={() => {
          fetchGroups(pagination.current_page || 1);
        }}
      />
      <WhatsappBusinessGroupDeleteGroupModal
        open={deleteGroupOpen}
        onClose={() => setDeleteGroupOpen(false)}
        groupName={deleteGroupName}
        onSuccess={() => {
          fetchGroups(pagination.current_page || 1);
        }}
      />
      <WhatsappBusinessGroupEdit
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setEditInitialGroup("");
        }}
        options={groups.map((g) => g.group_name)}
        initialGroupName={editInitialGroup}
        onSuccess={() => fetchGroups(pagination.current_page || 1)}
      />
    </Box>
  );
};

export default WhatsappBusinessGroupsManage;

